import { Alert, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/button/button';
import Drawer from '@/components/drawer/drawer';
import FactTile from '@/components/fact-tile/fact-tile';
import Loader from '@/components/loader/loader';
import { useOutboundCall } from '@/hooks/use-outbound-calls/use-outbound-calls';
import { OutboundCall } from '@/services/outbound-calls-service/calls.service.types';
import { formatNumber } from '@/i18n/format';
import { calculateSecondsToMinutes } from '@/utils/helpers';
import { languageName } from '@/utils/language-name';
import { CallOutcome, CallResult } from '../call-result';
import { outboundErrorMessage } from '../outbound-calls.messages';
import { formatDateTime, transferResultLabel, triggerSourceLabel } from '../outbound-calls.utils';
import styles from '../outbound-calls.module.scss';

interface CallDrawerProps {
  callId?: number;
  onClose: () => void;
  onOpenLead: (leadId: number) => void;
}

function CallBody({ call, onOpenLead }: { call: OutboundCall; onOpenLead: (id: number) => void }) {
  const { t } = useTranslation('outbound');
  const none = t('none');
  const { lead } = call;

  return (
    <Flex vertical gap={16}>
      <CallOutcome
        disposition={call.disposition}
        objectiveCompleted={call.objective_completed}
        live={call.status === 'live'}
      />

      <div className={styles.drawerFacts}>
        <FactTile label={t('call.facts.scenario')} value={call.scenario_name ?? none} />
        <FactTile label={t('call.facts.campaign')} value={call.campaign?.name ?? none} />
        <FactTile
          label={t('call.facts.source')}
          value={[triggerSourceLabel(call.trigger_source), call.triggered_by]
            .filter(Boolean)
            .join(' · ')}
        />
        <FactTile
          label={t('call.facts.duration')}
          value={call.duration ? calculateSecondsToMinutes(call.duration) : none}
        />
        <FactTile
          label={t('call.facts.language')}
          value={call.language ? languageName(call.language) : none}
        />
        <FactTile
          label={t('call.facts.transfer')}
          value={transferResultLabel(call.transfer_result)}
        />
        {call.transferred_to && (
          <FactTile label={t('call.facts.transferredTo')} value={call.transferred_to.name} />
        )}
        {call.transfer_attempts > 0 && (
          <FactTile
            label={t('call.facts.transferAttempts')}
            value={formatNumber(call.transfer_attempts)}
          />
        )}
      </div>

      <CallResult call={call} />

      {lead && (
        <div>
          <Button buttonVariant="secondary" onClick={() => onOpenLead(lead.id)}>
            {t('call.showLead')}
          </Button>
        </div>
      )}
    </Flex>
  );
}

function CallDrawerContents({ callId, onClose, onOpenLead }: Required<CallDrawerProps>) {
  const { t } = useTranslation('outbound');
  const { call, error } = useOutboundCall(callId);

  let body = <Loader />;
  if (call) body = <CallBody call={call} onOpenLead={onOpenLead} />;
  else if (error)
    body = <Alert type="error" showIcon message={outboundErrorMessage(error, t('errors.load'))} />;

  return (
    <Drawer
      open
      onClose={onClose}
      title={call?.lead?.name ?? t('call.title', { phone: call?.phone ?? '' })}
      meta={
        call && (
          <>
            <span>{formatDateTime(call.created_at)}</span>
            {call.phone && <b>{call.phone}</b>}
            {call.contact && (
              <span>
                {[call.contact.full_name, call.contact.position].filter(Boolean).join(', ')}
              </span>
            )}
          </>
        )
      }
    >
      {body}
    </Drawer>
  );
}

/** One call: its outcome, the hand-over to a salesperson and what the agent collected. */
export function CallDrawer({ callId, onClose, onOpenLead }: CallDrawerProps) {
  if (!callId) return null;
  return <CallDrawerContents callId={callId} onClose={onClose} onOpenLead={onOpenLead} />;
}
